import React, { useState, useCallback, useEffect } from 'react';
import { Card } from '../types/game';
import { CardComponent } from './Game';
import { COLORS } from './Game';
import styled, { css } from 'styled-components';
import { isValidSet } from '../utils/gameUtils';

interface DailyChallengeBoardProps {
    cards: Card[];
}

const Board = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: repeat(3, 1fr);
    gap: 10px;
    margin: 20px 0;
    max-width: 1000px;
    margin: 20px auto;

    @media (max-width: 768px) {
        grid-template-columns: repeat(3, 1fr);
        grid-template-rows: repeat(4, 1fr);
        gap: 8px;
        margin: 15px auto;
    }
`;

const CompletedSets = styled.div`
    margin-top: 20px;
    padding: 10px;
    background-color: #f8f9fa;
    border: 1px solid #ddd;
    border-radius: 5px;
    font-family: monospace;
    white-space: pre-wrap;
    width: 100%;
    font-size: 14px;

    @media (max-width: 768px) {
        font-size: 12px;
        margin-top: 15px;
        padding: 8px;
    }
`;

const Shape = styled.div<{ color: string; shading: string }>`
    position: absolute;
    width: 25%;
    height: 25%;
    display: flex;
    justify-content: center;
    align-items: center;

    svg {
        height: 100%;
        width: 100%;
        fill: ${props => {
            switch (props.shading) {
                case 'solid':
                    return props.color;
                case 'dotted':
                    return `url(#dotted-${Object.entries(COLORS).find(([_, value]) => value === props.color)?.[0]})`;
                case 'crosshatched':
                    return `url(#crosshatch-${Object.entries(COLORS).find(([_, value]) => value === props.color)?.[0]})`;
                default:
                    return 'none';
            }
        }};
        stroke: ${props => props.color};
        stroke-width: 2;
    }

    @media (max-width: 768px) {
        width: 20%;
        height: 20%;
    }

    @media (max-width: 480px) {
        width: 15%;
        height: 15%;
    }
`;

const DailyChallengeBoard: React.FC<DailyChallengeBoardProps> = ({ cards }) => {
    const [selectedCards, setSelectedCards] = useState<Card[]>([]);
    const [completedSets, setCompletedSets] = useState<Card[][]>([]);
    const [shakingCards, setShakingCards] = useState<string[]>([]);

    const handleCardClick = useCallback((card: Card) => {
        setSelectedCards(prevSelected => {
            const isSelected = prevSelected.some(selected => selected.id === card.id);
            if (isSelected) {
                return prevSelected.filter(selected => selected.id !== card.id);
            } else {
                const newSelected = [...prevSelected, card];
                return newSelected.length <= 3 ? newSelected : prevSelected;
            }
        });
    }, []);

    useEffect(() => {
        if (selectedCards.length === 3) {
            const timer = setTimeout(() => {
                if (isValidSet(selectedCards)) {
                    const isAlreadyCompleted = completedSets.some(set =>
                        set.every(c => selectedCards.some(nc => nc.id === c.id))
                    );
                    if (!isAlreadyCompleted) {
                        setCompletedSets([...completedSets, selectedCards]);
                        console.log('Valid set found:', selectedCards);
                    } else {
                        console.log('Set already completed:', selectedCards);
                    }
                } else {
                    console.log('Not a valid set:', selectedCards);
                    setShakingCards(selectedCards.map(card => card.id));
                    setTimeout(() => setShakingCards([]), 500);
                }
                setSelectedCards([]);
            }, 200);

            return () => clearTimeout(timer);
        }
    }, [selectedCards, completedSets]);

    return (
        <div>
            <Board>
                {cards.map(card => (
                    <CardComponent
                        key={card.id}
                        card={card}
                        selected={selectedCards.some(selected => selected.id === card.id)}
                        isMatched={completedSets.some(set => set.some(c => c.id === card.id))}
                        isNew={false}
                        isShuffling={false}
                        onClick={() => handleCardClick(card)}
                        shake={shakingCards.includes(card.id)}
                    />
                ))}
            </Board>
            <CompletedSets>
                <h3>Completed Sets</h3>
                {completedSets.length === 0 ? (
                    <p>No sets found yet.</p>
                ) : (
                    completedSets.map((set, index) => (
                        <div key={index} style={{ marginTop: '10px' }}>
                            <strong>Set {index + 1}:</strong>
                            {set.map(card => (
                                <div key={card.id} style={{ marginLeft: '20px' }}>
                                    {card.number} {card.color} {card.shape} {card.shading}
                                </div>
                            ))}
                        </div>
                    ))
                )}
            </CompletedSets>
        </div>
    );
};

export default DailyChallengeBoard; 